import React, { useState, useEffect, useRef } from "react";
import { X } from "lucide-react";

export const openOurStoryModal = () => {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("open-our-story-modal"));
  }
};

export const OurStoryModal = ({ isOpen: controlledIsOpen, onClose: controlledOnClose }) => {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const videoRef = useRef(null);

  const isControlled = typeof controlledIsOpen === "boolean";
  const isOpen = isControlled ? controlledIsOpen : internalIsOpen;

  const handleClose = () => {
    if (isControlled && controlledOnClose) {
      controlledOnClose();
    } else {
      setInternalIsOpen(false);
    }
    if (videoRef.current) {
      videoRef.current.pause();
    }
  };

  useEffect(() => {
    const handleOpenEvent = () => {
      setInternalIsOpen(true);
    };

    window.addEventListener("open-our-story-modal", handleOpenEvent);
    return () => {
      window.removeEventListener("open-our-story-modal", handleOpenEvent);
    };
  }, []);

  // Handle Play/Pause on modal open/close
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      if (videoRef.current) {
        videoRef.current.currentTime = 0;
        const playPromise = videoRef.current.play();
        if (playPromise !== undefined) {
          playPromise.catch((err) => {
            console.log("Autoplay was prevented by browser:", err);
          });
        }
      }
    } else {
      document.body.style.overflow = "unset";
      if (videoRef.current) {
        videoRef.current.pause();
      }
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  // Handle Escape key to close
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && isOpen) {
        handleClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[99999] flex items-center justify-center p-3 sm:p-6 lg:p-8 animate-fadeIn"
      style={{
        backgroundColor: "rgba(0, 0, 0, 0.78)",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
      }}
      onClick={handleClose}
    >
      {/* Modal Card with exact background image & styling */}
      <div
        className="relative w-full max-w-[960px] lg:max-w-[1040px] xl:max-w-[1100px] rounded-[24px] sm:rounded-[32px] overflow-hidden border border-white/20 shadow-[0_25px_70px_rgba(0,10,50,0.8)] p-4 sm:p-6 lg:p-7 flex flex-col gap-3 sm:gap-4 animate-scaleUp"
        style={{
          backgroundImage: "url('/images/our-story-modal-bg.jpg')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundColor: "#03194A",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top Header Row with Close Button at Top-Right */}
        <div className="flex items-center justify-end w-full">
          <button
            onClick={handleClose}
            aria-label="Close modal"
            className="flex h-10 w-10 sm:h-11 sm:w-11 items-center justify-center rounded-full border border-white/70 bg-white/10 text-white transition-all duration-200 hover:bg-white/25 hover:scale-105 active:scale-95 cursor-pointer shadow-md"
          >
            <X className="h-5 w-5 sm:h-6 sm:w-6" strokeWidth={2.2} />
          </button>
        </div>

        {/* Video Player Container */}
        <div className="relative w-full aspect-video rounded-[16px] sm:rounded-[22px] overflow-hidden bg-black shadow-2xl border border-white/10">
          <video
            ref={videoRef}
            src="/videos/our-story.mp4"
            autoPlay
            playsInline
            controls
            className="w-full h-full object-contain bg-black"
          />
        </div>
      </div>
    </div>
  );
};

export default OurStoryModal;
